import type { PlanInputFormFixedProduct, PlanInputFormUsageProduct } from '@vy/types';
import {
  PRICING_MODELS,
  UPLIFT_DURATION_ERROR_MESSAGE,
  UPLIFT_FLAG_KEYS,
  hasUpliftEnabled,
  isMultiYearDuration,
} from '@vy/types';
import { isNotEmptyString, isNotNullOrUndefined, isNullOrUndefined, isNumber, oz, vyDate } from '@vy/utils';
import { getContractEndDate } from '../../../create-plan-page-services/get-contract-end-date';
import { ZERO_DURATION_MONTHS } from '../../../create-plan-page-consts/create-plan-default-values';

/**
 * Product-level rules the API enforces (NewPlanSchema / NewContractSchemaWithPlan)
 * but the form's deep-partial input schemas don't. Mirroring them here surfaces
 * the errors inline — on drawer Save and on step navigation — instead of as an
 * end-of-flow toast or an API rejection.
 */

type FixedShapedArrayName = 'fixedProducts' | 'promotionProducts';

type PlanInputFormProducts = {
  startDate?: string | Date | null;
  fixedProducts?: PlanInputFormFixedProduct[];
  promotionProducts?: PlanInputFormFixedProduct[];
  usageProducts?: PlanInputFormUsageProduct[];
  billingData?: { duration: number };
};

export function addProductConfigurationIssues(data: PlanInputFormProducts, ctx: oz.RefinementCtx): void {
  const duration = data.billingData?.duration;

  addFixedShapedProductIssues('fixedProducts', data.fixedProducts, ctx);
  addFixedShapedProductIssues('promotionProducts', data.promotionProducts, ctx);
  addUsageProductIssues(data.usageProducts, ctx);
  addScheduledInstallmentIssues(data.startDate, duration, data.fixedProducts, ctx);

  if (isNumber(duration)) {
    addUpliftDurationIssues('fixedProducts', data.fixedProducts, duration, ctx);
    addUpliftDurationIssues('promotionProducts', data.promotionProducts, duration, ctx);
    addUpliftDurationIssues('usageProducts', data.usageProducts, duration, ctx);
  }
}

function addFixedShapedProductIssues(
  arrayName: FixedShapedArrayName,
  products: PlanInputFormFixedProduct[] | undefined,
  ctx: oz.RefinementCtx,
): void {
  products?.forEach((product, productIndex) => {
    product.variants.forEach((variant, variantIndex) => {
      const priceConfigurationPath = [
        arrayName,
        productIndex,
        'variants',
        variantIndex,
        'pricing',
        'priceConfiguration',
      ];
      const priceConfiguration = variant.pricing.priceConfiguration;

      const discountConfiguration =
        isNotNullOrUndefined(priceConfiguration) && 'discountConfiguration' in priceConfiguration
          ? priceConfiguration.discountConfiguration
          : undefined;

      if (isNotNullOrUndefined(discountConfiguration)) {
        if (!isNotEmptyString(discountConfiguration.name)) {
          ctx.addIssue({
            code: oz.ZodIssueCode.custom,
            path: [...priceConfigurationPath, 'discountConfiguration', 'name'],
            message: 'Discount name is required',
          });
        }

        if (isNumber(discountConfiguration.recurring) && discountConfiguration.recurring < 1) {
          ctx.addIssue({
            code: oz.ZodIssueCode.custom,
            path: [...priceConfigurationPath, 'discountConfiguration', 'recurring'],
            message: 'Recurring times must be at least 1',
          });
        }
      }

      if (variant.pricing.pricingModel === PRICING_MODELS.Refund) {
        const price =
          isNotNullOrUndefined(priceConfiguration) && 'price' in priceConfiguration
            ? priceConfiguration.price
            : undefined;

        if (!isNumber(price)) {
          ctx.addIssue({
            code: oz.ZodIssueCode.custom,
            path: [...priceConfigurationPath, 'price'],
            message: 'Fixed amount is required',
          });
        }
      }
    });
  });
}

function addUsageProductIssues(products: PlanInputFormUsageProduct[] | undefined, ctx: oz.RefinementCtx): void {
  products?.forEach((product, productIndex) => {
    product.variants.forEach((variant, variantIndex) => {
      const priceConfiguration = variant.pricing.priceConfiguration;
      // Bulk size, when set, must be positive. Covers stale persisted values
      // (e.g. a 0 written by the pre-guard bulk-size input).
      const chunkSize =
        isNotNullOrUndefined(priceConfiguration) && 'chunkSize' in priceConfiguration
          ? priceConfiguration.chunkSize
          : undefined;

      if (isNumber(chunkSize) && chunkSize <= 0) {
        ctx.addIssue({
          code: oz.ZodIssueCode.custom,
          path: [
            'usageProducts',
            productIndex,
            'variants',
            variantIndex,
            'pricing',
            'priceConfiguration',
            'chunkSize',
          ],
          message: 'Bulk size is required',
        });
      }
    });
  });
}

/**
 * Scheduled installments (commercial-terms products): each amount must be
 * positive, every date inside the contract window, and the breakdown must sum
 * to the item price. Mirrors the validation the legacy installments dialog
 * enforced before the inline redesign.
 */
function addScheduledInstallmentIssues(
  startDate: PlanInputFormProducts['startDate'],
  duration: number | undefined,
  products: PlanInputFormFixedProduct[] | undefined,
  ctx: oz.RefinementCtx,
): void {
  products?.forEach((product, productIndex) => {
    const priceConfiguration = product.variants[0].pricing.priceConfiguration;
    const hasPrice = isNotNullOrUndefined(priceConfiguration) && 'price' in priceConfiguration;
    const scheduledInstallments =
      isNotNullOrUndefined(priceConfiguration) && 'scheduledInstallments' in priceConfiguration
        ? priceConfiguration.scheduledInstallments
        : undefined;

    if (isNullOrUndefined(scheduledInstallments) || !hasPrice) {
      return;
    }

    const price = priceConfiguration.price ?? 0;
    const installments = scheduledInstallments.installments ?? [];
    const installmentsPath = [
      'fixedProducts',
      productIndex,
      'variants',
      0,
      'pricing',
      'priceConfiguration',
      'scheduledInstallments',
      'installments',
    ];

    const contractStartDate = isNotNullOrUndefined(startDate) ? vyDate(startDate).toISOString() : undefined;
    const minimumStartDate = isNotNullOrUndefined(product.startDate)
      ? vyDate(product.startDate).toISOString()
      : contractStartDate;
    const contractEndDate = isNotNullOrUndefined(contractStartDate)
      ? getContractEndDate(contractStartDate, duration ?? ZERO_DURATION_MONTHS)
      : undefined;

    installments.forEach((installment, installmentIndex) => {
      if ((installment.amount ?? 0) <= 0) {
        ctx.addIssue({
          code: oz.ZodIssueCode.custom,
          path: [...installmentsPath, installmentIndex, 'amount'],
          message: 'Amount must be greater than 0',
        });
      }

      if (
        isNotNullOrUndefined(minimumStartDate) &&
        isNotNullOrUndefined(contractEndDate) &&
        isNotNullOrUndefined(installment.scheduledDateStr)
      ) {
        const installmentDate = vyDate(installment.scheduledDateStr);
        const isWithinContract =
          installmentDate.isSameOrAfter(vyDate(minimumStartDate), 'day') &&
          installmentDate.isBefore(vyDate(contractEndDate));

        if (!isWithinContract) {
          ctx.addIssue({
            code: oz.ZodIssueCode.custom,
            path: [...installmentsPath, installmentIndex, 'scheduledDateStr'],
            message: 'Installment date must be within the contract period',
          });
        }
      }
    });

    const installmentsTotal = installments.reduce((sum, item) => sum + (item.amount ?? 0), 0);
    if (Math.abs(installmentsTotal - price) >= 0.01) {
      ctx.addIssue({
        code: oz.ZodIssueCode.custom,
        path: [...installmentsPath],
        message: 'Installments must add up to the item price',
      });
    }
  });
}

function addUpliftDurationIssues(
  arrayName: FixedShapedArrayName | 'usageProducts',
  products: (PlanInputFormFixedProduct | PlanInputFormUsageProduct)[] | undefined,
  duration: number,
  ctx: oz.RefinementCtx,
): void {
  if (isMultiYearDuration(duration)) {
    return;
  }

  products?.forEach((product, productIndex) => {
    if (!hasUpliftEnabled(product)) {
      return;
    }

    UPLIFT_FLAG_KEYS.forEach((flag) => {
      if (product.upliftConfiguration?.[flag]) {
        ctx.addIssue({
          code: oz.ZodIssueCode.custom,
          path: [arrayName, productIndex, 'upliftConfiguration', flag],
          message: UPLIFT_DURATION_ERROR_MESSAGE,
        });
      }
    });
  });
}
